import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import type { VisibleLayer } from '~/types/dataModel'
import {
  CALIFORNIA_COAST_VIEW_STATE,
  COSMOS_SITE_ADAPTATION_CODES,
  COSMOS_SITE_VIEW_STATES,
  DEFAULT_MAX_ZOOM,
  type AdaptationCode,
  type CosmosSite,
  type Dataset,
  type GraphId,
  INITIAL_VIEW_STATE,
  MAP_FLY_DURATION_MS,
  MAX_VIEW_STATE_UPDATE_THROTTLE_MS,
  COSMOS_RETURN_PERIODS,
  VisibleLayers,
  type BasemapType,
  DEM_SCENARIOS,
  ADAPTATION_SCENARIOS,
  DEPTHS,
  RETURN_PERIODS,
  type WaveUnit,
  WAVES_OPTIONS,
  isErosionSite,
} from '~/constants'
import type { MapRef } from 'react-map-gl/maplibre'
import type { MapGeoJSONFeature } from 'maplibre-gl'
import {
  getRegionFromURL,
  getTrackedParamsFromURL,
  regionSlugFor,
  setRegionInURL,
} from '~/util/trackedURLParams'
import { notifyAdaptationChanged } from '~/util/mapTooltipActorBridge'
import { shouldShowWavesIntroOnDatasetChange } from './shouldShowWavesIntro'
import { throttle, zipObject } from 'lodash-es'
import { ViewStateSchema, type ViewState } from '~/util/viewState'
import type { MapboxOverlay } from '@deck.gl/mapbox'

export type AppState = {
  locationId: number | null
  setLocationId: (locationId: number) => void
  viewState: ViewState
  setViewState: (viewState: unknown) => void
  mapRef: MapRef | null
  setMapRef: (map: MapRef) => void
  /** Camera target requested before the map finished loading; applied by setMapRef. */
  pendingView: ViewState | null
  deckRef: MapboxOverlay | null
  setDeckRef: (deck: MapboxOverlay) => void
  impactPanelCollapsed: boolean
  setImpactPanelCollapsed: (collapsed: boolean) => void
  menuPanelCollapsed: boolean
  setMenuPanelCollapsed: (collapsed: boolean) => void
  introModalOpen: boolean
  setIntroModalOpen: (open: boolean) => void
  methodologyModalOpen: boolean
  setMethodologyModalOpen: (open: boolean) => void
  selectedBuildings: MapGeoJSONFeature[]
  selectedRoads: MapGeoJSONFeature[]
  setSelectedBuildings: (features: MapGeoJSONFeature[]) => void
  clearSelectedBuildings: () => void
  setSelectedRoads: (features: MapGeoJSONFeature[]) => void
  clearSelectedRoads: () => void
  layerVisibility: Record<VisibleLayer, boolean>
  toggleLayerVisibility: (source: VisibleLayer) => void
  setLayerVisibility: (source: VisibleLayer, visible: boolean) => void
  selectedFeatureProperties: GeoJSON.GeoJsonProperties | null
  setSelectedFeatureProperties: (properties: GeoJSON.GeoJsonProperties | null) => void
  activeFeatureId: number | null
  setActiveFeatureId: (featureId: number | null) => void
  maxZoom: number | undefined
  basemapType: BasemapType
  setBasemapType: (basemapType: BasemapType) => void
  forceShowIntroModal: boolean
  setForceShowIntroModal: (force: boolean) => void
  hasShownWavesIntroThisSession: boolean
  hasRegionInURL: boolean
  getCosmosScenario: () => string | undefined
  returnPeriod: string | undefined
  DEM: string | undefined
  adaptation: string | undefined
  setReturnPeriod: (returnPeriod: string) => void
  setDEM: (dem: string) => void
  setAdaptation: (adaptation: string) => void
  dataset: Dataset
  setDataset: (dataset: Dataset) => void
  cosmosSite: CosmosSite
  setCosmosSite: (site: CosmosSite) => void
  enterFromIntro: (destination: CosmosSite | 'waves' | 'erosion') => void
  wavesProperty: WaveUnit
  setWavesProperty: (property: WaveUnit) => void
  depths: number[]
  setDepth: (depth: number) => void
  overlayPanelVisible: boolean
  setOverlayPanelVisible: (visible: boolean) => void
  selectedGraphType: GraphId | null
  setSelectedGraphType: (graphType: GraphId | null) => void
  hsReturnPeriod: string
  setHsReturnPeriod: (period: string) => void
  hsYear: '2055' | '2085'
  setHsYear: (year: '2055' | '2085') => void
  dynamicWavesProperty: WaveUnit | null
  contourFloodEnabled: boolean
  setContourFloodEnabled: (enabled: boolean) => void
  /** When an adaptation is selected: true = purple averted damages, false = red adapted damages. */
  avertedDamagesEnabled: boolean
  setAvertedDamagesEnabled: (enabled: boolean) => void
}

function computeDynamicWavesProperty(
  state: Pick<AppState, 'wavesProperty' | 'hsYear' | 'hsReturnPeriod'>
): WaveUnit | null {
  const { wavesProperty, hsYear, hsReturnPeriod } = state
  if (wavesProperty !== 'hs_change') return null
  if (!hsReturnPeriod || !hsYear) return null
  // 2025 is always compared against whatever the user chooses
  return `ratio_rp_${hsReturnPeriod}_${hsYear}_2025_ENSEMBLE_percentage_change` as WaveUnit
}

const useAppStore = create<AppState>()(
  subscribeWithSelector(
    devtools(
      (set, get) => {
        const {
          adaptation,
          basemapType,
          bathymetryVisible,
          dataset,
          dem,
          depths,
          maxZoom,
          returnPeriod,
          viewState,
          wavesProperty,
          hsReturnPeriod,
          hsYear,
        } = getTrackedParamsFromURL()

        const regionSlug = getRegionFromURL()
        const regionDataset: Dataset | undefined = regionSlug
          ? regionSlug === 'calwaves'
            ? 'waves'
            : regionSlug === 'erosion'
              ? 'erosion'
              : 'cosmos'
          : undefined

        const regionSite =
          regionSlug && regionSlug !== 'calwaves' && regionSlug !== 'erosion'
            ? regionSlug
            : undefined

        const resolvedDataset = dataset ?? regionDataset ?? 'cosmos'
        const resolvedSite =
          resolvedDataset === 'erosion' && regionSite && !isErosionSite(regionSite)
            ? 'santacruz'
            : (regionSite ?? 'santacruz')

        const regionViewState = regionSlug
          ? regionSlug === 'calwaves'
            ? CALIFORNIA_COAST_VIEW_STATE
            : COSMOS_SITE_VIEW_STATES[resolvedSite]
          : undefined

        // The map only reaches the store once it has loaded, so a camera move requested
        // before then (e.g. picking a region in the intro modal) is held and applied on load.
        const flyToView = (view: ViewState) => {
          const { mapRef } = get()
          if (!mapRef) {
            set(() => ({ pendingView: view }))
            return
          }
          mapRef.flyTo({
            center: [view.longitude, view.latitude],
            zoom: view.zoom,
            duration: MAP_FLY_DURATION_MS,
          })
        }

        return {
          forceShowIntroModal: false,
          setForceShowIntroModal: (force: boolean) => {
            set(() => ({ forceShowIntroModal: force }))
          },
          hasShownWavesIntroThisSession: false,
          hasRegionInURL: regionSlug != null,
          locationId: null,
          setLocationId: (locationId: number) => {
            set(() => ({ locationId }))
          },
          viewState: viewState ?? regionViewState ?? INITIAL_VIEW_STATE,
          setViewState: throttle((viewState: unknown) => {
            const { data: newViewState, error } = ViewStateSchema.safeParse(viewState)
            if (error) {
              console.debug({ error })
            }
            if (newViewState) {
              set(() => ({ viewState: newViewState }))
            }
          }, MAX_VIEW_STATE_UPDATE_THROTTLE_MS),
          mapRef: null,
          setMapRef: (map) => {
            const { pendingView } = get()
            set(() => ({ mapRef: map, pendingView: null }))
            if (pendingView) {
              map.jumpTo({
                center: [pendingView.longitude, pendingView.latitude],
                zoom: pendingView.zoom,
              })
            }
          },
          pendingView: null,
          deckRef: null,
          setDeckRef: (deck) => set(() => ({ deckRef: deck })),
          impactPanelCollapsed: true,
          setImpactPanelCollapsed: (collapsed: boolean) => {
            set(() => ({ impactPanelCollapsed: collapsed }))
          },
          menuPanelCollapsed: false,
          setMenuPanelCollapsed: (collapsed: boolean) => {
            set(() => ({ menuPanelCollapsed: collapsed }))
          },
          introModalOpen: true,
          setIntroModalOpen: (open: boolean) => {
            set(() => ({ introModalOpen: open }))
          },
          methodologyModalOpen: false,
          setMethodologyModalOpen: (open: boolean) => {
            set(() => ({ methodologyModalOpen: open }))
          },
          selectedBuildings: [],
          selectedRoads: [],
          setSelectedBuildings: (features: MapGeoJSONFeature[]) =>
            set(() => ({
              selectedBuildings: features,
            })),
          clearSelectedBuildings: () => set(() => ({ selectedBuildings: [] })),
          setSelectedRoads: (features: MapGeoJSONFeature[]) =>
            set(() => ({
              selectedRoads: features,
            })),
          clearSelectedRoads: () => set(() => ({ selectedRoads: [] })),
          layerVisibility: (() => {
            // If bathymetryVisible is explicitly set in URL, use that value
            // Otherwise, default to hidden
            const INVISIBLE_LAYERS = bathymetryVisible === true ? [] : [VisibleLayers.BATHYMETRY]

            function isVisible(layer: VisibleLayer) {
              // @ts-ignore
              return !INVISIBLE_LAYERS.includes(layer)
            }
            const layers = Object.values(VisibleLayers)
            const visibilities = layers.map(isVisible)
            return zipObject(layers, visibilities) as Record<VisibleLayer, boolean>
          })(),
          toggleLayerVisibility: (layer: VisibleLayer) =>
            set((state) => ({
              layerVisibility: {
                ...state.layerVisibility,
                [layer]: !state.layerVisibility[layer],
              },
            })),
          setLayerVisibility: (layer: VisibleLayer, visible: boolean) =>
            set((state) => ({
              layerVisibility: {
                ...state.layerVisibility,
                [layer]: visible,
              },
            })),
          selectedFeatureProperties: null,
          setSelectedFeatureProperties: (properties: GeoJSON.GeoJsonProperties | null) =>
            set(() => ({
              selectedFeatureProperties: properties,
            })),
          activeFeatureId: null,
          setActiveFeatureId: (featureId: number | null) =>
            set(() => ({ activeFeatureId: featureId })),
          maxZoom: maxZoom ?? DEFAULT_MAX_ZOOM,
          basemapType: basemapType ?? ('SATELLITE' as BasemapType),
          setBasemapType: (basemapType: BasemapType) => {
            set(() => ({ basemapType }))
          },
          returnPeriod: returnPeriod ?? COSMOS_RETURN_PERIODS[0],
          DEM: dem ?? DEM_SCENARIOS[0],
          adaptation: adaptation ?? ADAPTATION_SCENARIOS[0],
          getCosmosScenario: () => {
            const { returnPeriod, DEM, adaptation } = get()
            if (returnPeriod && DEM && adaptation) {
              return `RP${returnPeriod}_DEM${DEM}_ADAPT${adaptation}`
            }
            return undefined
          },
          setReturnPeriod: (returnPeriod: string) => {
            set(() => ({ returnPeriod }))
          },
          setDEM: (dem: string) => {
            set(() => ({ DEM: dem }))
          },
          setAdaptation: (adaptation: string) => {
            const prev = get().adaptation
            if (prev === adaptation) {
              return
            }
            set(() => ({ adaptation }))
            notifyAdaptationChanged()
          },
          dataset: resolvedDataset,
          cosmosSite: resolvedSite,
          setCosmosSite: (site: CosmosSite) => {
            const { adaptation, dataset } = get()
            const allowed = COSMOS_SITE_ADAPTATION_CODES[site]
            const adaptationWillChange =
              !!adaptation && !allowed.includes(adaptation as AdaptationCode)
            set(() => ({
              cosmosSite: site,
              ...(adaptationWillChange ? { adaptation: '000' } : {}),
            }))
            if (adaptationWillChange) {
              notifyAdaptationChanged()
            }
            setRegionInURL(regionSlugFor(dataset, site))
            flyToView(COSMOS_SITE_VIEW_STATES[site])
          },
          enterFromIntro: (destination: CosmosSite | 'waves' | 'erosion') => {
            const { setDataset, setCosmosSite } = get()
            if (destination === 'waves') {
              setDataset('waves')
            } else if (destination === 'erosion') {
              setDataset('erosion')
            } else {
              setDataset('cosmos')
              setCosmosSite(destination)
            }
            set(() => ({ introModalOpen: false, hasShownWavesIntroThisSession: true }))
          },
          setDataset: (dataset: Dataset) => {
            const { dataset: prevDataset, hasShownWavesIntroThisSession, cosmosSite } = get()
            const shouldShowWavesIntro = shouldShowWavesIntroOnDatasetChange(
              dataset,
              prevDataset,
              hasShownWavesIntroThisSession
            )

            const nextSite =
              dataset === 'erosion' && !isErosionSite(cosmosSite) ? 'santacruz' : cosmosSite

            set(() => ({
              dataset,
              ...(nextSite !== cosmosSite ? { cosmosSite: nextSite } : {}),
              ...(shouldShowWavesIntro && {
                introModalOpen: true,
                hasShownWavesIntroThisSession: true,
              }),
            }))

            setRegionInURL(regionSlugFor(dataset, nextSite))

            // Zoom to appropriate view based on dataset selection
            if (dataset === 'waves') {
              flyToView(CALIFORNIA_COAST_VIEW_STATE)
            } else if (dataset === 'erosion' || dataset === 'cosmos') {
              // Adaptation and erosion share a site, so keep the user's viewport when
              // toggling between them unless the site itself changed.
              const isSiteDatasetToggle =
                (prevDataset === 'erosion' || prevDataset === 'cosmos') && nextSite === cosmosSite
              if (isSiteDatasetToggle) {
                return
              }
              flyToView(COSMOS_SITE_VIEW_STATES[nextSite])
            }
          },
          wavesProperty: (wavesProperty as WaveUnit) ?? (WAVES_OPTIONS[0]?.value as WaveUnit),
          setWavesProperty: (property: WaveUnit) => {
            set(() => ({ wavesProperty: property }))
          },
          dynamicWavesProperty: null,
          depths: depths || DEPTHS,
          setDepth: (depth: number) => {
            set((state) => {
              const currentDepths = state.depths
              const index = currentDepths.indexOf(depth)
              if (index > -1) {
                // Remove depth if already selected
                return { depths: currentDepths.filter((d) => d !== depth) }
              } else {
                // Add depth if not selected
                return { depths: [...currentDepths, depth].sort((a, b) => a - b) }
              }
            })
          },
          overlayPanelVisible: true,
          setOverlayPanelVisible: (visible: boolean) => {
            set(() => ({ overlayPanelVisible: visible }))
          },
          selectedGraphType: null,
          setSelectedGraphType: (graphType: GraphId | null) => {
            set(() => ({ selectedGraphType: graphType }))
            // Automatically reopen overlay panel when a graph type is selected
            if (graphType !== null) {
              set(() => ({ overlayPanelVisible: true }))
            }
          },
          hsReturnPeriod: hsReturnPeriod ?? RETURN_PERIODS[0],
          setHsReturnPeriod: (period: string) => {
            set(() => ({ hsReturnPeriod: period }))
          },
          // User must always compare to 2025
          hsYear: hsYear ?? '2055',
          setHsYear: (year: '2055' | '2085') => {
            set(() => ({ hsYear: year }))
          },
          contourFloodEnabled: false,
          setContourFloodEnabled: (enabled: boolean) => {
            set(() => ({ contourFloodEnabled: enabled }))
          },
          avertedDamagesEnabled: false,
          setAvertedDamagesEnabled: (enabled: boolean) => {
            set(() => ({ avertedDamagesEnabled: enabled }))
          },
        }
      },
      {
        // Install Redux DevTools Extension to view app state in browser:
        // https://chromewebstore.google.com/detail/redux-devtools/lmhkpmbekcpmknklioeibfkpmmfibljd?pli=1
        name: 'ISS', // This name will appear in Redux DevTools
        // The extension serializes the whole state on every action. mapRef/deckRef hold
        // MapLibre and deck.gl object graphs (tile caches, WebGL handles) that must not
        // be serialized. Strip them first.
        stateSanitizer: <S>(state: S): S =>
          ({
            ...(state as unknown as AppState),
            mapRef: '<<MapRef (excluded from DevTools)>>',
            deckRef: '<<MapboxOverlay (excluded from DevTools)>>',
          }) as unknown as S,
      }
    )
  )
)

useAppStore.subscribe(
  (s) => `${s.wavesProperty}|${s.hsReturnPeriod}|${s.hsYear}`,
  () => {
    const state = useAppStore.getState()
    const nextDynamic = computeDynamicWavesProperty(state)
    if (state.dynamicWavesProperty !== nextDynamic) {
      useAppStore.setState({ dynamicWavesProperty: nextDynamic })
    }
  },
  { fireImmediately: true }
)

export default useAppStore
